import { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  MenuItem,
  ListSubheader,
  Select,
  FormControl,
  Typography,
  ListItemIcon,
  IconButton,
  Tooltip
} from '@mui/material';
import Check from '@mui/icons-material/Check';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';

const normalize = (str = '') =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const CustomSelect = ({
  items = [],
  selected = [],
  onChange = () => {},
  label = 'Selecionar',
  allowNone = true,
  allowAll = false,
  inheritColor = false,
  multiple = false,
  orderAsc = true,
  onToggleOrderAsc = () => {},
  maxVisibleLabels = Infinity,
  disabled = false,
  enableSearch = false,
  enabledToggle = false,
  layout = 'normal',
  scrollToSelected = false,
  margin = 2
}) => {
  const theme = useTheme();
  const [selecionados, setSelecionados] = useState(multiple ? [] : '');
  const lastPartialSelectionRef = useRef([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSelecionados(selected);
  }, [selected]);

  const allIds = useMemo(() => {

    return items
      .filter(item => !item.disabled)
      .map(item => item.id);

  }, [items]);

  const isAllSelected = useMemo(() => {
    return multiple && selecionados.length === allIds.length;
  }, [selecionados, allIds, multiple]);

  const handleChange = (e) => {
    const value = e.target.value;

    if (!allowNone) {
      if (multiple && value.length === 0) return;
      else if (!multiple && value === '') return;
    }

    if (multiple) {
      // só salva quando for seleção parcial válida
      if (value.length > 0 && !isAllSelected) {
        lastPartialSelectionRef.current = value;
      }
    }

    setSelecionados(value);
    onChange(value);
  };

  const handleDoubleClick = () => {
    if (!multiple || !enabledToggle) return;
    const hasSelection = selecionados.length > 0;

    let newValue;

    if (allowNone) {
      
      // 🔹 NOVA REGRA (permite vazio)
      if (hasSelection) {
        // salva antes de limpar
        lastPartialSelectionRef.current = selecionados;
        newValue = [];
      } else {
        newValue =
          lastPartialSelectionRef.current?.length
            ? lastPartialSelectionRef.current
            : [];
      }
    } else {
      // 🔹 REGRA ANTIGA (não permite vazio)
      if (isAllSelected) {
        newValue =
          lastPartialSelectionRef.current?.length
            ? lastPartialSelectionRef.current
            : [allIds[allIds.length - 1]];
      } else {
        newValue = allIds;
      }
    }

    setSelecionados(newValue);
    onChange(newValue);
  };

  const sharedColor = inheritColor ? 'inherit' : theme.palette.text.primary;
  const sharedBackground = inheritColor ? 'inherit !important' : theme.palette.background.paper;
  const sharedBorder = inheritColor ? '1px solid currentColor' : `1px solid ${theme.palette.text.primary}`;

  const normalizedItems = useMemo(() => {
    return items.map(i => ({
      ...i,
      labelNormalized: normalize(i.label)
    }));
  }, [items]);

const filteredItems = useMemo(() => {

  if (!enableSearch) {
    return normalizedItems;
  }

  const s = normalize(search);

  const selectedIds = new Set(
    multiple
      ? (selecionados || [])
      : [selecionados]
  );

  const keepSelected =
    !multiple || selecionados.length === 1;

  return normalizedItems.filter(i => {

    if (i.labelNormalized.includes(s)) {
      return true;
    }

    return keepSelected && selectedIds.has(i.id);

  });

}, [
  normalizedItems,
  search,
  enableSearch,
  selecionados,
  multiple
]);

  const longestLabel = items.reduce(
    (max, i) => Math.max(max, i.label.length),
    0
  );

  const handleClear = (e) => {
    e.stopPropagation();

    if (!Array.isArray(selecionados) || selecionados.length === 0) return;
    lastPartialSelectionRef.current = selecionados;

    setSelecionados([]);
    onChange([]);
  };

  const handleAll = (e) => {
    e.stopPropagation();

    if (
      !Array.isArray(selecionados) ||
      isAllSelected
    ) {
      return;
    }

    lastPartialSelectionRef.current =
      selecionados;

    const enabledIds =
      items
        .filter(item => !item.disabled)
        .map(item => item.id);

    setSelecionados(enabledIds);
    onChange(enabledIds);
  };

  const showClear =
    multiple && enabledToggle && allowNone;

  const showAll = 
    multiple && allowAll;

  const isEmpty =
    !Array.isArray(selecionados) || selecionados.length === 0;

  const isAll =
    !Array.isArray(selecionados) || isAllSelected;

  const itemRefs = useRef(new Map());

  useEffect(() => {
    if (!open) return;
    if (!scrollToSelected) return;

    let id = null;

    if (multiple) {
      if (!Array.isArray(selecionados) || selecionados.length !== 1) return;
      id = selecionados[0];
    } else {
      if (!selecionados) return;
      id = selecionados;
    }

    const tryFocus = () => {
      const el = itemRefs.current.get(id);

      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'center' });
      } else {
        requestAnimationFrame(tryFocus);
      }
    };

    requestAnimationFrame(tryFocus);

  }, [open, selecionados, scrollToSelected, multiple]);

  const isMainLoading = useLoadingStore(selectIsMainLoading);

  const isDisabled = disabled || isMainLoading;

  const selectMinWidth = useMemo(() => {
    const baseWidth = layout === 'normal' ? 166 : 100;

    const charWidth =
      layout === 'compact' ? 6 : 7;

    const labelPadding =
      layout === 'compact' ? 24 : 32;

    const itemPadding = 48;

    const longestItemLength = items.reduce(
      (max, item) =>
        Math.max(max, String(item.label || '').length),
      0
    );

    return Math.max(
      baseWidth,
      String(label || '').length * charWidth + labelPadding,
      longestItemLength * charWidth + itemPadding
    );
  }, [items, label, layout]);

  return (
    <Box
      m={margin}
      sx={{
        minWidth: selectMinWidth,
        backgroundColor: sharedBackground,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
        [theme.breakpoints.down(800)]: { flexGrow: 1 }
      }}
      onDoubleClick={() => {
        if (!multiple || open) return;
        handleDoubleClick();
      }}
      onClick={(e) => {
        if (!multiple) return;

        if (e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          handleDoubleClick();
        }
      }}
    >
      <FormControl fullWidth sx={{ 
        minWidth: selectMinWidth,
        position: 'relative',
        backgroundColor: sharedBackground 
      }}>
        {layout !== 'minor' &&
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              top: layout === 'compact' ? -7 : -10,
              left: layout === 'compact' ? 8 : 12,
              backgroundColor: sharedBackground,
              px: layout === 'compact' ? 0.5 : 1,
              py: layout === 'compact' ? 0 : 0.2,
              fontSize: layout === 'compact' ? 10 : 12,
              color: sharedColor,
              zIndex: 1,
              pointerEvents: 'none',
              borderRadius: 1,
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Typography>
      }

        <Select
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          multiple={multiple}
          MenuProps={{
            disableScrollLock: true,
            MenuListProps: {
              autoFocus: false,
              autoFocusItem: false,
              disableListWrap: true,
              sx: {
                pt: enableSearch ? 0 : 1
              }
            }
          }}
          value={selecionados}
          onChange={handleChange}
          displayEmpty
          renderValue={(selectedValue) => {
            const ids = (multiple ? selectedValue : [selectedValue])
              .filter(v => v !== '' && v !== undefined && v !== null);

            if (!multiple) {
              const item = items.find(i => i.id === ids[0]);
              return item ? item.label : '';
            }

            // se passou do limite, mostra só "X selecionados"
            if (Number.isFinite(maxVisibleLabels) && ids.length > maxVisibleLabels) {
              const count = ids.length;
              return `${count} selecionado${count > 1 ? 's' : ''}`;
            }

            // caso contrário, lista os labels
            const labels = ids
              .map(id => items.find(i => i.id === id)?.label)
              .filter(Boolean);

            return labels.join(', ');
          }}
          autoWidth={false}
          sx={{
            color: sharedColor,
            background: sharedBackground,
            cursor: isDisabled ? 'not-allowed' : 'pointer',

            ...(layout !== 'normal' && {
              border: 'none',
              padding: 0,

              '& .MuiSelect-select': {
                  padding:
                      layout === 'compact'
                          ? '5px 24px 3px 8px !important'
                          : '2px 24px 2px 8px !important'
              },
              
              '& .MuiSelect-icon': { color: sharedColor, right: '0 !important' }
            }),

            border: sharedBorder,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${sharedColor} !important`
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${sharedColor} !important`
            },

            ...(layout === 'normal' && {
              '& .MuiSelect-icon': { color: sharedColor }
            })

            
          }}
        >
          {(enableSearch || showClear || showAll) && (
            <ListSubheader
              disableSticky={false}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: theme.palette.background.custonSelect,
                cursor: 'default',
                minWidth: `${longestLabel * 8 + 120}px`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                gap: 1,
                p: 1
              }}
            >
              {enableSearch && (
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    outline: 'none'
                  }}
                />
              )}

              {showClear && (
                <Tooltip title={isEmpty ? 'Nenhum selecionado.' : 'Limpar seleção.'}>
                  <IconButton
                    onClick={handleClear}
                    color='error'
                    variant='contained'
                    sx={{
                      p: 0.5,
                      fontSize: 12,
                      cursor: isEmpty ? 'not-allowed' : 'pointer',
                      opacity: isEmpty ? 0.4 : 1,
                      userSelect: 'none'
                    }}
                    
                  >
                    <DeleteSweepIcon/>
                  </IconButton>
                </Tooltip>
              )}

              {showAll && (
                <Tooltip title={isAll ? 'Já estão todos selecionados.' : 'Selecionar todos.'}>
                  <IconButton
                    onClick={handleAll}
                    color='success'
                    variant='contained'
                    sx={{
                      p: 0.5,
                      fontSize: 12,
                      cursor: isAll ? 'not-allowed' : 'pointer',
                      opacity: isAll ? 0.4 : 1,
                      userSelect: 'none'
                    }}
                    
                  >
                    <SelectAllIcon/>
                  </IconButton>
                </Tooltip>
              )}

            </ListSubheader>
          )}
          {filteredItems.map((item) => {
            const isSelected = multiple ? selecionados.includes(item.id) : selecionados === item.id;
            return (
              <MenuItem
                key={item.id}
                value={item.id}
                disabled={item.disabled === true}
                ref={(el) => {
                  if (el) itemRefs.current.set(item.id, el);
                }}
                onClick={() => {
                  const isAlreadySelected = multiple ? selecionados.includes(item.id) : selecionados === item.id;
                  if (!multiple && isAlreadySelected && item.showArrow) {
                    onToggleOrderAsc();
                  }
                }}
                sx={{
                  color: sharedColor,
                  backgroundColor: isSelected ? theme.palette.action.selected : sharedBackground,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                {isSelected && (
                  <ListItemIcon sx={{ minWidth: '24px !important', color: sharedColor }}>
                    <Check fontSize="small" />
                  </ListItemIcon>
                )}
                {item.label}

                {item.secondaryText && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{
                            ml: 1,
                            opacity: 0.7,
                            fontStyle: 'italic'
                        }}
                    >
                        ({item.secondaryText})
                    </Typography>
                )}
                {item.showArrow && isSelected && (orderAsc ? <ArrowDropUpIcon fontSize="large" /> : <ArrowDropDownIcon fontSize="large" />)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};


export default CustomSelect;